import { evaluateNpsState } from '@arnold/common';
import { countAnswer, createChoiceSummary, questionSummaryKey, type ReportMetricCalculation } from '@arnold/core';
import { QuestionType } from '../../generated/hooks';
import { isScoringValid, resolveQuestionScoring } from './questionScoring';
import type {
  AnswerGroupLabels,
  MessageRef,
  NewReportData,
  MetricSummary,
  QuestionScoringMap,
  QuestionSummary,
  ReportQuestion,
  TextEntry,
  ThemeSummary,
} from './types';

export function createAnswerGroupContext(
  data: NewReportData,
  manualScoring: QuestionScoringMap,
  labels: AnswerGroupLabels,
  customMetrics: ReportMetricCalculation[] = [],
) {
  const scoring = resolveQuestionScoring(data.questions, manualScoring);
  const teamsById = new Map(data.teams.map((team) => [String(team.id), team]));
  const respondentsById = new Map(data.respondents.map((respondent) => [String(respondent.id), respondent]));
  const responsesById = new Map(data.responses.map((response) => [String(response.id), response]));
  const responseIdByMessage = new Map(
    data.responses.flatMap((response) =>
      (response.messages ?? []).map((message) => [message.id, response.id] as const),
    ),
  );
  const responsesByRespondent = new Map<string, NewReportData['responses']>();
  const teamByRespondent = new Map<string, string>();
  const anonymousRespondentIds = new Set<string>();
  const themesByMessage = new Map<string, string[]>();
  const questionsByIndex = new Map(data.questions.map((question) => [question.index, question]));
  const validScoring = new Map(
    data.questions.map((question) => [
      question.index,
      {
        average: isScoringValid(question, scoring[question.index]?.average),
        nps: isScoringValid(question, scoring[question.index]?.nps),
      },
    ]),
  );

  for (const team of data.teams) {
    for (const id of team.directMembers ?? []) {
      teamByRespondent.set(String(id), String(team.id));
    }
  }
  const hasPublicSurvey = data.surveys.some((survey) => survey.public);
  for (const respondent of data.respondents) {
    if (hasPublicSurvey || !`${respondent.firstname} ${respondent.surname}`.trim()) {
      anonymousRespondentIds.add(String(respondent.id));
    }
  }
  for (const response of data.responses) {
    const id = String(response.respondentId);
    const responses = responsesByRespondent.get(id) ?? [];
    responses.push(response);
    responsesByRespondent.set(id, responses);
    if (
      !respondentsById.has(id) ||
      response.anonymous ||
      response.originalAnonymous ||
      response.additionallyAnonymized ||
      response.messages?.some((message) => message.anonymous || message.originalAnonymous)
    ) {
      anonymousRespondentIds.add(id);
    }
  }
  for (const question of data.questionLabels) {
    for (const theme of question.questionLabelComments ?? []) {
      for (const id of theme.commentIds ?? []) {
        const key = `${question.questionDefinitionIndex}:${id}`;
        const labels = themesByMessage.get(key) ?? [];
        if (!labels.includes(theme.label)) labels.push(theme.label);
        themesByMessage.set(key, labels);
      }
    }
  }
  return {
    data,
    customMetrics,
    responseIdByMessage,
    scoring,
    labels,
    teamsById,
    respondentsById,
    responsesById,
    responsesByRespondent,
    teamByRespondent,
    anonymousRespondentIds,
    themesByMessage,
    questionsByIndex,
    validScoring,
  };
}

export type AnswerGroupContext = ReturnType<typeof createAnswerGroupContext>;

const emptyMetric = (): MetricSummary => ({
  status: 'unconfigured',
  count: 0,
  sum: 0,
  min: 0,
  max: 1,
  state: 'normal',
});

function emptySummary(question: ReportQuestion, statementIndex?: number): QuestionSummary {
  const counts = createChoiceSummary(question, statementIndex);
  return {
    ...counts,
    type: question.type,
    choices: counts.choices.map((choice) => ({ ...choice, percentage: 0 })),
    selectedLabels: [],
    average: emptyMetric(),
    nps: {
      ...emptyMetric(),
      min: -100,
      max: 100,
      inputMin: 0,
      detractors: 0,
      passives: 0,
      promoters: 0,
      percentages: [0, 0, 0],
    },
    textEntries: [],
    commentEntries: [],
    themes: { text: [], comment: [] },
  };
}

function addMessage(summary: QuestionSummary, message: MessageRef, context: AnswerGroupContext) {
  const themeLabels = context.themesByMessage.get(`${message.questionDefinitionIndex}:${message.id}`) ?? [];
  const responseId = context.responseIdByMessage.get(message.id);
  const response = responseId ? context.responsesById.get(responseId) : undefined;
  const respondentId = response ? String(response.respondentId) : undefined;
  const respondent = respondentId ? context.respondentsById.get(respondentId) : undefined;
  const teamId = respondentId ? context.teamByRespondent.get(respondentId) : undefined;
  const team = teamId ? context.teamsById.get(teamId) : undefined;
  const isAnonymous = respondentId ? context.anonymousRespondentIds.has(respondentId) : true;
  const author =
    !isAnonymous && respondent ? `${respondent.firstname ?? ''} ${respondent.surname ?? ''}`.trim() || null : null;
  const teamName = team?.teamName || null;
  if (summary.type === QuestionType.Freetext && message.text?.trim()) {
    summary.textEntries.push({ message, source: 'text', themes: themeLabels, author, teamName });
  }
  if (message.comment?.trim()) {
    summary.commentEntries.push({ message, source: 'comment', themes: themeLabels, author, teamName });
  }
  // Validation and counting are shared with the API's metrics snapshot.
  const indexes = countAnswer(summary, message, responseId);
  if (!indexes) return;

  for (const kind of ['average', 'nps'] as const) {
    const scoring = context.scoring[summary.questionIndex]?.[kind];
    if (!scoring || !context.validScoring.get(summary.questionIndex)?.[kind]) continue;
    const score = scoring.optionScores[indexes[0]];
    if (score === null) continue;
    summary[kind].sum += score;
    summary[kind].count++;
    if (kind === 'nps') {
      if (score <= 6) summary.nps.detractors++;
      else if (score <= 8) summary.nps.passives++;
      else summary.nps.promoters++;
    }
  }
}

function countThemes(entries: TextEntry[]): ThemeSummary[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    for (const label of entry.themes) counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function finalizeSummary(summary: QuestionSummary, context: AnswerGroupContext): QuestionSummary {
  const scoring = context.scoring[summary.questionIndex];
  summary.choices.forEach((choice) => {
    choice.percentage = summary.validCount ? (100 * choice.count) / summary.validCount : 0;
  });
  summary.selectedLabels = summary.choices.filter((choice) => choice.count > 0).map((choice) => choice.label);
  for (const kind of ['average', 'nps'] as const) {
    const metric = summary[kind];
    metric.status = !scoring?.[kind]
      ? 'unconfigured'
      : !context.validScoring.get(summary.questionIndex)?.[kind]
        ? 'invalid'
        : metric.count
          ? 'available'
          : 'empty';
    if (metric.status === 'available') {
      metric.value =
        kind === 'nps'
          ? Math.round((100 * (summary.nps.promoters - summary.nps.detractors)) / metric.count)
          : metric.sum / metric.count;
      metric.state = kind === 'nps' ? evaluateNpsState(metric.value) : 'normal';
    }
  }
  summary.average.min = scoring?.average?.min ?? 0;
  summary.average.max = scoring?.average?.max ?? 1;
  summary.nps.inputMin = scoring?.nps?.minValue ?? 0;
  summary.nps.percentages = [summary.nps.detractors, summary.nps.passives, summary.nps.promoters].map((count) =>
    summary.nps.count ? (100 * count) / summary.nps.count : 0,
  ) as [number, number, number];
  // Question-local lexical order: never line up a person's anonymous answers across columns.
  const sortEntries = (entries: TextEntry[]) =>
    entries.sort((a, b) => (a.message[a.source] ?? '').localeCompare(b.message[b.source] ?? ''));
  sortEntries(summary.textEntries);
  sortEntries(summary.commentEntries);
  summary.themes = { text: countThemes(summary.textEntries), comment: countThemes(summary.commentEntries) };
  return summary;
}

export function summarizeAnswers(
  answers: Map<string, MessageRef[]>,
  context: AnswerGroupContext,
): Map<string, QuestionSummary> {
  const summaries = new Map<string, QuestionSummary>();
  for (const [questionIndex, messages] of answers) {
    const question = context.questionsByIndex.get(questionIndex);
    if (!question) continue;
    for (const message of messages) {
      const statements = message.statementIndex == null ? [undefined] : [undefined, message.statementIndex];
      for (const statementIndex of statements) {
        const key = questionSummaryKey(questionIndex, statementIndex);
        const summary = summaries.get(key) ?? emptySummary(question, statementIndex);
        addMessage(summary, message, context);
        summaries.set(key, summary);
      }
    }
  }
  summaries.forEach((summary) => finalizeSummary(summary, context));
  return summaries;
}

/** Inputs must be disjoint; the group factory checks overlapping response membership before merging. */
export function mergeQuestionSummaries(
  maps: Map<string, QuestionSummary>[],
  context: AnswerGroupContext,
): Map<string, QuestionSummary> {
  const summaries = new Map<string, QuestionSummary>();
  for (const map of maps) {
    for (const [key, source] of map) {
      const target =
        summaries.get(key) ?? emptySummary(context.questionsByIndex.get(source.questionIndex)!, source.statementIndex);
      target.validCount += source.validCount;
      source.respondingChannelIds.forEach((id) => target.respondingChannelIds.add(id));
      source.choiceResponseIds.forEach((ids, index) => ids.forEach((id) => target.choiceResponseIds[index].add(id)));
      target.escapeCount += source.escapeCount;
      target.invalidCount += source.invalidCount;
      for (const kind of ['average', 'nps'] as const) {
        target[kind].sum += source[kind].sum;
        target[kind].count += source[kind].count;
      }
      target.nps.detractors += source.nps.detractors;
      target.nps.passives += source.nps.passives;
      target.nps.promoters += source.nps.promoters;
      source.choices.forEach((choice, index) => {
        target.choices[index].count += choice.count;
      });
      target.textEntries.push(...source.textEntries);
      target.commentEntries.push(...source.commentEntries);
      summaries.set(key, target);
    }
  }
  summaries.forEach((summary) => finalizeSummary(summary, context));
  return summaries;
}
